import {
  Avatar,
  Badge,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from "@flpstudio/design-system";
import parseHtml from "html-react-parser";
import DOMPurify from "isomorphic-dompurify";

import type { CommentDto } from "@/api/openapi";
import { Skeleton } from "@/components/atoms/Loading/Skeleton";
import { TextOverflowWrapper } from "@/components/atoms/TextOverflowWrapper";
import { Like } from "@/components/molecules/Like/Like";
import { useAuth } from "@/hooks/use-auth";
import { useComments } from "@/hooks/use-comment";
import { useExpertFullProfileSearchParams } from "@/hooks/use-page-search-params";
import { formatDate } from "@/utils/format-date";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { ActionMenu } from "./ActionMenu";
import { CommentForm } from "./CommentForm";
import { DeleteConfirmation } from "./DeleteConfirmation";
import {
  ModerateConfirmationDialog,
  type Action as ModeratorAction,
} from "./ModerateConfirmationDialog";
import { UserBadge } from "./UserBadge";

type Props = {
  commentGroupId: string;
  authorId?: string;
  onPublish?: (values: { commentCount: number }) => void;
  onMenuClick?: (values: {
    menu: "edit" | "delete";
    commentCount: number;
  }) => void;
  onDelete?: (values: { confirmed: boolean; commentCount: number }) => void;
  onLike?: (values: {
    userType: "client" | "expert";
    liked: boolean;
    singleCommentReactionCount: number;
    commentCount: number;
  }) => void;
  onUserClick?: (values: {
    userType: "client" | "expert";
    singleCommentReactionCount: number;
    commentCount: number;
  }) => void;
};

export function CommentList(props: Props) {
  const { setExpertIdentifier } = useExpertFullProfileSearchParams();

  const { user, role } = useAuth();

  const { data, isLoading } = useComments({
    groupId: props.commentGroupId,
    withHiddenComments: role === "admin",
  });

  const [isDeleteConfirmationOpen, deleteConfirmationHandler] = useDisclosure();
  const [commentToDelete, setCommentToDelete] = useState("");
  const [commentToEdit, setCommentToEdit] = useState("");
  const [commentToModerate, setCommentToModerate] = useState<{
    id: string;
    action: ModeratorAction;
  }>({ id: "", action: "HIDE" });

  if (isLoading) {
    return <Skeleton className="mt-6" repeat={5} />;
  }

  if (!data?.length) {
    return (
      <Text className="mt-6 text-center text-[color:--mantine-color-gray-6] text-sm/normal">
        Be the first to post
      </Text>
    );
  }

  const handleViewUser = (comment: CommentDto) => {
    if (comment.postedBy?.isExpert) {
      setExpertIdentifier(comment.postedBy.urlId || comment.postedBy.userId);
    }
    props.onUserClick?.({
      userType: comment.postedBy?.isExpert ? "expert" : "client",
      singleCommentReactionCount: comment.likeCount,
      commentCount: data.length,
    });
  };

  return (
    <Stack className="mt-6 max-h-dvh gap-6 overflow-y-auto">
      {data.map((comment) => (
        <div key={comment.id} className="flex gap-2">
          <UnstyledButton
            aria-label="View user profile"
            className="self-start"
            onClick={() => handleViewUser(comment)}
            disabled={!comment.postedBy?.isExpert} // Only enabled for expert users for now
          >
            <Avatar src={comment.postedBy?.profileImage} alt="Profile" />
          </UnstyledButton>
          <Stack className="grow gap-1">
            <Group className="gap-2">
              {comment.postedBy?.name ? (
                <>
                  <UnstyledButton
                    onClick={() => handleViewUser(comment)}
                    disabled={!comment.postedBy?.isExpert} // Only enabled for expert users for now
                    className="font-medium hover:text-[--mantine-primary-color-filled-hover] hover:underline disabled:cursor-default disabled:hover:text-current disabled:hover:no-underline"
                  >
                    {comment.postedBy.name}
                  </UnstyledButton>
                </>
              ) : (
                <Text className="text-[color:--mantine-color-gray-6] italic">
                  User deactivated
                </Text>
              )}
              {comment.hidden ? (
                <Badge variant="light" color="gray">
                  Hidden
                </Badge>
              ) : (
                <UserBadge
                  userType={
                    user?.id === comment.postedBy?.userId
                      ? "me"
                      : props.authorId === comment.postedBy?.userId
                        ? "author"
                        : comment.postedBy?.isExpert
                          ? "expert"
                          : null
                  }
                />
              )}
              <ActionMenu
                comment={comment as CommentDto}
                onMenuClick={(menu) => {
                  switch (menu) {
                    case "EDIT":
                      setCommentToEdit(comment.id);
                      break;
                    case "DELETE":
                      setCommentToDelete(comment.id);
                      deleteConfirmationHandler.open();
                      break;
                    case "HIDE":
                      setCommentToModerate({
                        id: comment.id,
                        action: "HIDE",
                      });
                      break;
                    case "UNHIDE":
                      setCommentToModerate({
                        id: comment.id,
                        action: "UNHIDE",
                      });
                      break;
                    default:
                      console.warn(`Unhandled menu action: ${menu}`);
                  }
                }}
              />
            </Group>
            <Text className="text-[color:--mantine-color-gray-6] text-sm">
              {formatDate(new Date(comment.createdAt))}
            </Text>
            {commentToEdit === comment.id ? (
              <CommentForm
                commentId={comment.id}
                commentGroupId={props.commentGroupId}
                defaultValue={comment.comment}
                onCancel={() => setCommentToEdit("")}
                onPublish={() => {
                  props.onPublish?.({
                    commentCount: data.length,
                  });
                }}
              />
            ) : (
              <>
                <TextOverflowWrapper>
                  {parseHtml(
                    DOMPurify.sanitize(
                      comment.comment.replaceAll("\n", "<br />"),
                      { ALLOWED_TAGS: ["br"] },
                    ),
                  )}
                </TextOverflowWrapper>
                <Like
                  count={comment.likeCount}
                  likedByMe={comment.likedByMe}
                  likeGroupType="COMMENT"
                  groupId={comment.likeGroup}
                  readOnly={!user}
                  onSubmit={({ liked }) =>
                    props.onLike?.({
                      liked,
                      commentCount: data.length,
                      singleCommentReactionCount: comment.likeCount,
                      userType: comment.postedBy?.isExpert
                        ? "expert"
                        : "client",
                    })
                  }
                />
              </>
            )}
          </Stack>
        </div>
      ))}
      <DeleteConfirmation
        commentId={commentToDelete}
        groupId={props.commentGroupId}
        opened={isDeleteConfirmationOpen}
        onClose={(confirmed) => {
          props.onDelete?.({
            confirmed,
            commentCount: data.length,
          });
          deleteConfirmationHandler.close();
        }}
      />
      <ModerateConfirmationDialog
        commentId={commentToModerate.id}
        groupId={props.commentGroupId}
        action={commentToModerate.action}
        onClose={() => {
          setCommentToModerate({ id: "", action: "HIDE" });
        }}
      />
    </Stack>
  );
}
